import { useCallback, useState } from 'react';
import { useFileScan } from 'hooks/useFileScan';
import { useGTM } from 'hooks/useGTM';
import { useToastNotify } from '@opswat/ui';
import { useTranslation } from 'react-i18next';
import { ScanOptions } from 'services/interface';


const SCAN_OPTIONS_STORAGE_KEY = 'metadefender_scan_options';
const ARCHIVE_PASSWORD_SESSION_KEY = 'metadefender_archive_password';
const SELECTED_WORKFLOW_STORAGE_KEY = 'metadefender_selected_workflow';
const SELECTED_SCAN_TYPE_STORAGE_KEY = 'metadefender_selected_scan_type';

const DEFAULT_SCAN_OPTIONS: ScanOptions = {
    multiscanning: false,
    deepCdr: false,
    adaptiveSandbox: false,
    proactiveDlp: false,
    unarchiving: false,
    privateScan: false
};

export const useGlobalDragDrop = () => {
    const { scan } = useFileScan();
    const { trackClick } = useGTM();
    const { toastNotify } = useToastNotify();
    const { t } = useTranslation();
    const [isDragOver, setIsDragOver] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    interface StoredWorkflow {
        _id?: string;
        name?: string;
    }

    const getSelectedWorkflow = useCallback((): StoredWorkflow | null => {
        if (typeof window === 'undefined') return null;
        const saved = localStorage.getItem(SELECTED_WORKFLOW_STORAGE_KEY);
        if (saved) {
            try {
                return JSON.parse(saved);
            } catch {
                return null;
            }
        }
        return null;
    }, []);

    const getSelectedScanType = useCallback((): string => {
        if (typeof window === 'undefined') {
            return '';
        }
        return localStorage.getItem(SELECTED_SCAN_TYPE_STORAGE_KEY) ?? '';
    }, []);

    const buildRulesFromOptions = useCallback((options: ScanOptions) => {
        const rules: string[] = [];

        const isCdrOnly = options.deepCdr && !options.multiscanning && !options.proactiveDlp && !options.adaptiveSandbox;

        const shouldSanitizeForCdr = options.deepCdr && (options.multiscanning || options.proactiveDlp || options.unarchiving || options.adaptiveSandbox);

        if (isCdrOnly) {
            rules.push('cdr');
            return rules.join(', ');
        }

        if (shouldSanitizeForCdr) {
            rules.push('sanitize');
            if (options.proactiveDlp) {
                rules.push('dlp');
            }
        } else {
            if (options.multiscanning) {
                rules.push('multiscan');
            }
            if (options.proactiveDlp) {
                rules.push('dlp');
            }
        }

        if (options.unarchiving) {
            rules.push('unarchive');
        }

        return rules.join(', ');
    }, []);

    const handleFileSelect = useCallback((files: FileList | null) => {
        if (!files || files.length === 0) {
            return;
        }
        const file = files[0];
        if (file.size === 0) {
            toastNotify({
                title: t('Error'),
                message: t('Cannot upload an empty file.'),
                type: 'error',
                hasClose: true,
                duration: 4000
            });
            return;
        }

        setIsUploading(true);
        trackClick('global-drag-drop-file-upload', 'ui', 'File uploaded via global drag & drop');
        let persistedOptions = DEFAULT_SCAN_OPTIONS;
        const saved = typeof window !== 'undefined' ? localStorage.getItem(SCAN_OPTIONS_STORAGE_KEY) : null;
        if (saved) {
            const parsed = JSON.parse(saved);
            persistedOptions = { ...DEFAULT_SCAN_OPTIONS, ...parsed };
        }

        const selectedWorkflow = getSelectedWorkflow();
        const selectedScanType = getSelectedScanType();
        const isWorkflowScanType = selectedScanType === 'workflow';
        const rules = isWorkflowScanType && selectedWorkflow
            ? (selectedWorkflow.name || selectedWorkflow._id || '')
            : buildRulesFromOptions(persistedOptions);
        const adaptiveSandbox = !!persistedOptions.adaptiveSandbox;
        const privateScan = !!persistedOptions.privateScan;
        const savedPasswords = typeof window !== 'undefined' ? sessionStorage.getItem(ARCHIVE_PASSWORD_SESSION_KEY) : null;
        const archivePasswords = (savedPasswords ? JSON.parse(savedPasswords) : []) as string[];

        const shouldSendArchivePasswords = Boolean(
            archivePasswords.length > 0 && (persistedOptions.unarchiving || isWorkflowScanType)
        );

        scan(file, rules, adaptiveSandbox, privateScan, archivePasswords, undefined, undefined, shouldSendArchivePasswords);
    }, [buildRulesFromOptions, getSelectedScanType, getSelectedWorkflow, scan, t, toastNotify, trackClick]);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        const files = e.dataTransfer.files;
        handleFileSelect(files);
    }, [handleFileSelect]);

    return {
        isDragOver,
        isUploading,
        handleDragOver,
        handleDragLeave,
        handleDrop,
        handleFileSelect
    };
};
